// Story 2.10 — TanStack Query mutation for POST /api/v1/dossiers/:dropId/purchase.
//
// Per-submit idempotency key (regenerated per submit by the caller, NOT per
// modal mount — matches Stories 1.16/1.17/1.18 pattern). On success,
// invalidates wallet-balance + wallet-history (the debit must show up) +
// the vault-items grid (the new dossier must show in the collection).
//
// Typed error envelope carries the extra fields the backend echoes on the
// branched error codes (DOSSIER_PURCHASE_CAP_REACHED, DROP_CLOSED,
// INSUFFICIENT_BALANCE) so the modal can render contextual messaging
// without an extra round-trip.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

/** Ceiling on dossiers per checkout — mirrors the backend's
 *  MAX_PURCHASE_QUANTITY (the server bound is authoritative). */
export const MAX_PURCHASE_QUANTITY = 10;

export type PurchasedDossierItem = {
  dossier_id: string;
  edition_number: number;
};

export type PurchasedDossierResponseData = {
  /** First minted item (editions are sequential per checkout). */
  dossier_id: string;
  drop_id: string;
  /** First minted item's edition. */
  edition_number: number;
  /** UNIT price. */
  price_cents: number;
  quantity: number;
  total_price_cents: number;
  /** Every minted item, in edition order. */
  dossiers: PurchasedDossierItem[];
  acquired_at: string;
  balance_cents_after: number;
};

export type DossierPurchaseErrorCode =
  | "INSUFFICIENT_BALANCE"
  | "DOSSIER_PURCHASE_CAP_REACHED"
  | "DOSSIER_PURCHASE_DROP_CLOSED"
  | "IDEMPOTENCY_CONFLICT"
  | "IDEMPOTENCY_KEY_REQUIRED"
  | "BAD_REQUEST"
  | "DROP_NOT_FOUND"
  | "UNAUTHORIZED"
  | "UNKNOWN";

export type DossierPurchaseError = {
  code: DossierPurchaseErrorCode;
  message: string;
  // 422 INSUFFICIENT_BALANCE (Story 1.15 envelope).
  details?: { required_cents?: number; available_cents?: number };
  // 409 DOSSIER_PURCHASE_CAP_REACHED extras.
  per_user_cap?: number;
  user_owned_count?: number;
  // 409 DOSSIER_PURCHASE_DROP_CLOSED extras.
  current_status?: string;
  // 404 DROP_NOT_FOUND extras.
  drop_id?: string;
};

async function postPurchase(
  dropId: string,
  idempotencyKey: string,
  quantity: number,
): Promise<PurchasedDossierResponseData> {
  const response = await fetch(`/api/v1/dossiers/${dropId}/purchase`, {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-idempotency-key": idempotencyKey,
    },
    body: JSON.stringify({ quantity }),
  });
  attachRequestIdToScope(response);
  if (response.status === 401) {
    throw {
      code: "UNAUTHORIZED" as const,
      message: "Sign in to buy this dossier.",
    } satisfies DossierPurchaseError;
  }
  if (!response.ok) {
    let detail: DossierPurchaseError = {
      code: "UNKNOWN",
      message: `Purchase failed (HTTP ${response.status})`,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = { ...raw.error };
      }
    } catch {
      // Body not JSON.
    }
    throw detail;
  }
  const body = (await response.json()) as { data: PurchasedDossierResponseData };
  return body.data;
}

export function useDossierPurchase(dropId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    PurchasedDossierResponseData,
    DossierPurchaseError,
    { idempotencyKey: string; quantity?: number }
  >({
    mutationFn: ({ idempotencyKey, quantity }) =>
      postPurchase(dropId, idempotencyKey, quantity ?? 1),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wallet-balance"] });
      queryClient.invalidateQueries({ queryKey: ["wallet-history"] });
      // Story 4.2 keyed the vault grid ["vault-items", {filters…}] — the
      // prefix match invalidates every filter/sort/page combination, so
      // the purchase shows in the collection without a manual refresh.
      queryClient.invalidateQueries({ queryKey: ["vault-items"] });
    },
  });
}
